<script setup lang="ts">
/**
 * Editing prose in place, on the page.
 *
 * Text is edited where it will be printed, in the document's own type and
 * measure, not in a panel at the side. That is the difference between a
 * builder and a form: somebody writing a paragraph can see whether it fits,
 * how it sits against the table above it, and where the page will break.
 *
 * The toolbar appears only over a selection. A permanent toolbar over every
 * paragraph would put interface chrome in the middle of a document whose whole
 * purpose is to be looked at. It holds what is reached for while writing; the
 * rest (colours, highlights, sizes, alignment, links) is in the inspector,
 * which acts on this editor's selection through `useTextFormatting`.
 *
 * How the block is set (its columns, gap, leading, alignment) comes from the
 * element around the editor, as the same variables reading uses, so writing
 * and reading look alike.
 *
 * Everything leaving here is sanitised. The editor produces markup, and markup
 * that reaches `v-html` unchecked is a hole regardless of who typed it.
 */
import { useI18n } from 'vue-i18n'

import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import { Editor, EditorContent } from '@tiptap/vue-3'
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue'

import { PROSE_EXTENSIONS } from '@/components/builder/proseMarks'
import { TextStyleMark } from '@/components/builder/textStyleMark'
import { attachEditor } from '@/composables/useTextFormatting'
import { sanitiseHtml } from '@/domain/sanitise'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()
const report = useReportStore()

/** The named styles this document offers, empty for most reports. */
const textStyles = computed(() => report.layout.textStyles)

const props = defineProps<{
  html: string
  /** Focus as soon as it mounts: the section was just selected to be written. */
  autofocus?: boolean
}>()

const emit = defineEmits<{
  change: [html: string]
  /** Leaving the editor, so the caller can drop back to reading. */
  done: []
}>()

const editor = shallowRef<Editor | null>(null)
const bubble = ref<{ top: number; left: number } | null>(null)
const root = ref<HTMLElement | null>(null)

editor.value = new Editor({
  content: props.html,
  autofocus: props.autofocus ? 'end' : false,
  extensions: [
    StarterKit.configure({
      // A report's headings are sections in the outline, not inline markup.
      // Allowing them here would produce headings the contents page cannot
      // see and the outline cannot reorder.
      heading: false,
      // Nothing in a financial report is a code block.
      codeBlock: false,
      horizontalRule: false,
    }),
    Placeholder.configure({
      placeholder: () => t('editor.placeholder'),
      // Only the empty document, not every empty paragraph: a hint under each
      // blank line while somebody is drafting is noise.
      showOnlyWhenEditable: true,
      showOnlyCurrent: false,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      // Matches the sanitiser: a link it would strip should never be offered.
      protocols: ['http', 'https', 'mailto'],
    }),
    TextStyleMark,
    ...PROSE_EXTENSIONS,
  ],
  editorProps: {
    attributes: { class: 'prose', role: 'textbox', 'aria-multiline': 'true' },
  },
  onUpdate: ({ editor: instance }) => {
    emit('change', sanitiseHtml(instance.getHTML()))
  },
  onSelectionUpdate: () => {
    positionBubble()
  },
  onBlur: () => {
    bubble.value = null
  },
})

/**
 * Put the toolbar over the selection.
 *
 * Positioned against this component rather than the viewport, so it travels
 * with the page when the canvas scrolls or zooms.
 */
function positionBubble(): void {
  const instance = editor.value
  const container = root.value
  if (!instance || !container) return

  const { from, to } = instance.state.selection
  if (from === to) {
    bubble.value = null
    return
  }

  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    bubble.value = null
    return
  }

  const box = selection.getRangeAt(0).getBoundingClientRect()
  const frame = container.getBoundingClientRect()

  bubble.value = {
    top: box.top - frame.top - 40,
    left: box.left - frame.left + box.width / 2,
  }
}

/**
 * Take content set from outside, but never while somebody is typing.
 *
 * Undo replaces the document, and the editor has to follow. Writing back the
 * same content the editor just emitted would move the caret to the end on
 * every keystroke, so the comparison guards against that.
 */
watch(
  () => props.html,
  (incoming) => {
    const instance = editor.value
    if (!instance) return
    if (sanitiseHtml(instance.getHTML()) === incoming) return
    instance.commands.setContent(incoming, false)
  },
)

// The inspector's tools act on this editor while it is the one in use.
const detach = editor.value ? attachEditor(editor.value) : () => undefined

onBeforeUnmount(() => {
  detach()
  editor.value?.destroy()
})

function toggle(
  mark: 'bold' | 'italic' | 'strike' | 'underline' | 'superscript' | 'subscript',
): void {
  const chain = editor.value?.chain().focus()
  if (!chain) return
  if (mark === 'bold') chain.toggleBold().run()
  if (mark === 'italic') chain.toggleItalic().run()
  if (mark === 'strike') chain.toggleStrike().run()
  if (mark === 'underline') chain.toggleUnderline().run()
  if (mark === 'superscript') chain.toggleSuperscript().run()
  if (mark === 'subscript') chain.toggleSubscript().run()
}

/** Every mark off the selection, and its paragraphs back to the block's alignment. */
function plain(): void {
  editor.value?.chain().focus().unsetAllMarks().unsetAlign().run()
}

function toggleList(kind: 'bullet' | 'ordered'): void {
  const chain = editor.value?.chain().focus()
  if (!chain) return
  if (kind === 'bullet') chain.toggleBulletList().run()
  else chain.toggleOrderedList().run()
}

function setLink(): void {
  const instance = editor.value
  if (!instance) return

  const existing = instance.getAttributes('link')['href'] as string | undefined
  const entered = window.prompt(t('editor.linkPrompt'), existing ?? 'https://')

  if (entered === null) return
  if (entered.trim() === '') {
    instance.chain().focus().unsetLink().run()
    return
  }
  instance.chain().focus().setLink({ href: entered.trim() }).run()
}

function active(name: string): boolean {
  return editor.value?.isActive(name) ?? false
}

/** The named style covering the selection, if any, empty string for "none". */
function activeTextStyle(): string {
  const styleId = editor.value?.getAttributes('reportTextStyle')['styleId'] as string | undefined
  return styleId ?? ''
}

function chooseTextStyle(styleId: string): void {
  const chain = editor.value?.chain().focus()
  if (!chain) return
  if (styleId) chain.setReportTextStyle(styleId).run()
  else chain.unsetReportTextStyle().run()
}
</script>

<template>
  <div ref="root" class="editor" @keydown.escape="emit('done')">
    <div
      v-if="bubble"
      class="editor__bubble"
      :style="{ top: `${bubble.top}px`, left: `${bubble.left}px` }"
      role="toolbar"
      :aria-label="t('editor.formatting')"
    >
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('bold') }"
        :aria-label="t('editor.bold')"
        @mousedown.prevent="toggle('bold')"
      >
        <strong>B</strong>
      </button>
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('italic') }"
        :aria-label="t('editor.italic')"
        @mousedown.prevent="toggle('italic')"
      >
        <em>I</em>
      </button>
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('underline') }"
        :aria-label="t('editor.underline')"
        :title="t('editor.underline')"
        @mousedown.prevent="toggle('underline')"
      >
        <u>U</u>
      </button>
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('strike') }"
        :aria-label="t('editor.strike')"
        @mousedown.prevent="toggle('strike')"
      >
        <s>S</s>
      </button>
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('superscript') }"
        :aria-label="t('editor.superscript')"
        :title="t('editor.superscript')"
        @mousedown.prevent="toggle('superscript')"
      >
        x<sup>2</sup>
      </button>
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('subscript') }"
        :aria-label="t('editor.subscript')"
        :title="t('editor.subscript')"
        @mousedown.prevent="toggle('subscript')"
      >
        x<sub>2</sub>
      </button>
      <span class="editor__divider" aria-hidden="true" />
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('bulletList') }"
        :aria-label="t('editor.bulletList')"
        @mousedown.prevent="toggleList('bullet')"
      >
        &bull;&#8212;
      </button>
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('orderedList') }"
        :aria-label="t('editor.orderedList')"
        @mousedown.prevent="toggleList('ordered')"
      >
        1.
      </button>

      <template v-if="textStyles.length">
        <span class="editor__divider" aria-hidden="true" />
        <select
          class="editor__style"
          :aria-label="t('editor.textStyle')"
          :value="activeTextStyle()"
          @mousedown.stop
          @change="chooseTextStyle(($event.target as HTMLSelectElement).value)"
        >
          <option value="">{{ t('editor.textStyleNone') }}</option>
          <option v-for="style in textStyles" :key="style.id" :value="style.id">
            {{ style.name }}
          </option>
        </select>
      </template>

      <span class="editor__divider" aria-hidden="true" />
      <button
        type="button"
        class="editor__tool"
        :class="{ 'editor__tool--on': active('link') }"
        :aria-label="t('editor.link')"
        @mousedown.prevent="setLink"
      >
        {{ t('editor.link') }}
      </button>
      <button
        type="button"
        class="editor__tool"
        :aria-label="t('editor.plain')"
        :title="t('editor.plain')"
        @mousedown.prevent="plain"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M3 3.5h8M7 3.5l-2.5 9M3 13.5h4M10.5 9.5l3.5 3.5M14 9.5l-3.5 3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <EditorContent v-if="editor" :editor="editor" />
  </div>
</template>

<style scoped>
.editor {
  position: relative;
}

/* The caret and selection are the only chrome while typing. */
.editor :deep(.prose:focus) {
  outline: none;
}

.editor :deep(.prose) {
  column-count: var(--text-columns, 1);
  column-gap: var(--text-gap, 10mm);
  column-rule: var(--text-rule, none);
  line-height: var(--text-leading, inherit);
}

.editor :deep(.prose p) {
  margin: 0 0 0.72em;
  text-align: var(--page-align, justify);
  hyphens: var(--text-hyphens, auto);
  /* A heading or a short paragraph split across the column break is worse
     than the ragged column it would otherwise leave, so a paragraph moves to
     the next column whole when it does not fit rather than being cut. */
  break-inside: avoid-column;
}

.editor :deep(.prose p:last-child) {
  margin-bottom: 0;
}

/* A paragraph's own alignment over the block's. */
.editor :deep(.prose p.align-left) {
  text-align: left;
}

.editor :deep(.prose p.align-center) {
  text-align: center;
}

.editor :deep(.prose p.align-right) {
  text-align: right;
}

.editor :deep(.prose p.align-justify) {
  text-align: justify;
}

.editor :deep(.prose ul),
.editor :deep(.prose ol) {
  margin: 0 0 0.72em;
  padding-left: 1.4em;
}

/* The placeholder a genuinely empty paragraph shows, so an empty text
   section does not look like a rendering failure. */
.editor :deep(.prose p.is-editor-empty:first-child::before) {
  content: 'Write here';
  float: left;
  height: 0;
  color: var(--paper-ink-soft);
  font-style: italic;
  pointer-events: none;
}

.editor__bubble {
  position: absolute;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 1px;
  padding: 3px;
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
  transform: translateX(-50%);
  font-family: var(--font-ui);
}

.editor__tool {
  min-width: 26px;
  height: 24px;
  padding: 0 6px;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.editor__style {
  height: 24px;
  max-width: 120px;
  padding: 0 4px;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
}

.editor__style:hover {
  background: var(--panel-hover);
  color: var(--ink);
}

.editor__tool:hover {
  background: var(--panel-hover);
  color: var(--ink);
}

.editor__tool--on {
  background: var(--accent-quiet);
  color: var(--accent);
}

.editor__divider {
  width: 1px;
  height: 15px;
  margin: 0 3px;
  background: var(--hairline);
}
</style>
